import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Divider, Image, Spin, App } from 'antd';
import {
  GoogleOutlined,
  TrophyOutlined,
  SafetyCertificateOutlined,
  UserOutlined
} from '@ant-design/icons';
import { authAPI, handleApiError } from '../services/api';
import './Login.css';

const Login = () => {
  const { message } = App.useApp(); // Use Ant Design App context
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [processingCallback, setProcessingCallback] = useState(false);

  // Handle OAuth callback from Google
  const handleOAuthCallback = useCallback(async (code, state) => {
    setProcessingCallback(true);
    try {
      // Call backend callback endpoint using API client
      const data = await authAPI.handleGoogleCallback(code, state);

      if (data.status === 'success') {
        // Save token and user info (use consistent 'token' key)
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        message.success('Login berhasil! Selamat datang.');

        // Redirect based on role
        setTimeout(() => {
          const userRole = data.user.role || (data.user.is_admin ? 'admin' : 'user');

          if (userRole === 'admin') {
            navigate('/admin');
          } else if (userRole === 'verifier' || userRole === 'verificator') {
            navigate('/verificator');
          } else {
            navigate('/');
          }
        }, 1000);
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      const apiError = handleApiError(error);
      console.error('OAuth callback error:', apiError);
      message.error(apiError.message || 'Login gagal. Silakan coba lagi.');
      // Clear URL parameters
      navigate('/login', { replace: true });
    } finally {
      setProcessingCallback(false);
    }
  }, [message, navigate]);

  // Check if user is already logged in and handle OAuth errors
  useEffect(() => {
    // Check if user is already logged in (use consistent 'token' key)
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && !searchParams.get('code') && !searchParams.get('error')) {
      // Parse user data to determine role
      let userRole = 'user';
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userRole = user.role || (user.is_admin ? 'admin' : 'user');
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      // Redirect to appropriate panel based on role
      if (userRole === 'admin') {
        navigate('/admin', { replace: true });
      } else if (userRole === 'verifier' || userRole === 'verificator') {
        navigate('/verificator', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    // Handle OAuth error from backend redirect
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');

    if (error) {
      message.error(decodeURIComponent(errorMessage || 'Login gagal. Silakan coba lagi.'));
      // Clear URL parameters
      navigate('/login', { replace: true });
      return;
    }

    // Note: OAuth callback is now handled by backend redirect to /verification
    // The old callback handling code is kept for backward compatibility
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, [navigate, searchParams, handleOAuthCallback, message]);

  // Handle Google OAuth Login
  const handleGoogleLogin = async () => {
    setLoading(true);

    try {
      // Get authorization URL from backend
      const response = await authAPI.getGoogleLoginUrl();

      if (response.status === 'success') {
        // Redirect to Google authorization page
        window.location.href = response.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      const apiError = handleApiError(error);
      message.error(apiError.message || 'Gagal menginisiasi login Google');
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  // Show loading spinner while processing OAuth callback
  if (processingCallback) {
    return (
      <div className="login-container">
        <div className="login-content">
          <Card className="login-card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 20 }}>Memproses login...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="floating-icons">
          <TrophyOutlined className="float-icon icon-1" />
          <SafetyCertificateOutlined className="float-icon icon-2" />
          <UserOutlined className="float-icon icon-3" />
        </div>
      </div>

      <div className="login-content">
        <Card className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <Image
                src="/images/itera.png"
                alt="ITERA Logo"
                width={100}
                preview={false}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%23999'%3EITERA%3C/text%3E%3C/svg%3E"
              />
            </div>
            <h1>GAMIFIKASI #AI-ITERA</h1>
            <p className="login-subtitle">
              Sistem Verifikasi & Manajemen Konten AI
            </p>
          </div>

          <Divider />

          <div className="login-body">
            <div className="welcome-text">
              <h2>Selamat Datang!</h2>
              <p>
                Login untuk mengakses fitur verifikasi data dan manajemen sistem
              </p>
            </div>

            <div className="login-buttons">
              <Button
                type="primary"
                size="large"
                icon={<GoogleOutlined />}
                onClick={handleGoogleLogin}
                loading={loading}
                className="google-login-btn"
                block
              >
                Login dengan Google
              </Button>
            </div>

            <div className="login-info">
              <div className="info-item">
                <SafetyCertificateOutlined />
                <span>Login aman dengan Google OAuth 2.0</span>
              </div>
              <div className="info-item">
                <UserOutlined />
                <span>Khusus untuk admin dan verifikator</span>
              </div>
            </div>
          </div>

          <Divider />

          <div className="login-footer">
            <p>
              Tidak punya akses? Hubungi administrator
            </p>
            <Button
              type="link"
              onClick={() => navigate('/')}
              className="back-link"
            >
              Kembali ke Leaderboard
            </Button>
          </div>
        </Card>

        <div className="login-features">
          <div className="feature-card">
            <TrophyOutlined className="feature-icon" />
            <h3>Verifikasi Data</h3>
            <p>Verifikasi kontribusi konten AI dari hasil crawling</p>
          </div>
          <div className="feature-card">
            <SafetyCertificateOutlined className="feature-icon" />
            <h3>Manajemen Poin</h3>
            <p>Kelola sistem poin dan ranking mahasiswa/dosen</p>
          </div>
          <div className="feature-card">
            <UserOutlined className="feature-icon" />
            <h3>Dashboard Admin</h3>
            <p>Akses penuh ke statistik dan laporan sistem</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
